import {
  ColumnDataTypes,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  INSTALLED_TOOLS_FILTERABLE_FIELDS,
  type PersonalInstalledToolListItem,
} from '@kirin/api-client/contracts/account-shadow-ai-installed-tools';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import {
  NameCell,
  CategoryCell,
  FirstDetectedCell,
  LastSeenCell,
  CategoryFilterOption,
  StringOperators,
  EnumOperators,
  DateOperators,
  EmptyOperators,
} from '@/components/shared/shadow-ai';
import { queryKeys } from '@/hooks/api/queryKeys';
import { fetchPersonalInstalledToolsFilterOptions } from './api';
import { PERSONAL_INSTALLED_TOOLS_FILTER_URL_KEYS } from './constants';
import { TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER } from '@/components/shared/timestampColumnWidth';

// Local arrays so the shared operator consts stay untouched.
const NameOperators = [...StringOperators, ...EmptyOperators];
const CategorySeverityOperators = [...EnumOperators, ...EmptyOperators];
const DateOperatorsWithEmpty = [...DateOperators, ...EmptyOperators];

export function createPersonalInstalledToolsColumns(): ColumnConfig<PersonalInstalledToolListItem>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      renderCell: ({ row }) => <NameCell row={row} />,
      width: 200,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: NameOperators,
      statePersistence: {
        urlKey:
          PERSONAL_INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.NAME
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.personalInstalledAiToolsFilterOptions(
            'name',
            params
          ),
        queryFn: params =>
          fetchPersonalInstalledToolsFilterOptions('name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessor: 'category',
      renderCell: ({ row }) => <CategoryCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'category',
      sortKey: 'cat',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: CategorySeverityOperators,
      statePersistence: {
        urlKey:
          PERSONAL_INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.CATEGORY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.personalInstalledAiToolsFilterOptions(
            'category',
            params
          ),
        queryFn: params =>
          fetchPersonalInstalledToolsFilterOptions('category', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        labelFormatter: getShadowAiCategoryLabel,
        render: (option, isSelected) => (
          <CategoryFilterOption
            category={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'firstDetected',
      header: 'First Detected',
      timestamp: true,
      minWidth: TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER,
      accessor: 'firstDetected',
      renderCell: ({ row }) => <FirstDetectedCell row={row} />,
      sortable: true,
      sortField: 'firstDetected',
      sortKey: 'fd',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'lastSeen',
      header: 'Last Seen',
      timestamp: true,
      accessor: 'lastSeen',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: 'lastSeen',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.LAST_SEEN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
